// @flow
import isEqual from 'lodash/isEqual';
import merge from 'lodash/merge';
import type { Action } from 'app/common/redux/types';
import type { AggregatedComment } from 'app/packages/pinterest-flow-types/comment';

export const COMMENT_OBJECT_TYPES = {
  aggregatedComment: 'aggregatedComment',
  didIt: 'didIt',
  pin: 'pin',
};

export type CommentObjectType = 'aggregatedComment' | 'didIt' | 'pin';

export type State = {
  [id: string]: AggregatedComment,
};

const initialState = {};

const mergeAggregatedComment = (currentItem, updatedItem) => {
  const mergedItem = {
    ...merge({}, currentItem, updatedItem),
    ...(updatedItem.content ? { content: updatedItem.content } : {}),
    ...(updatedItem.reaction_counts ? { reaction_counts: updatedItem.reaction_counts } : {}),
    ...(updatedItem.tags ? { tags: updatedItem.tags } : {}),
    ...(updatedItem.tagged_users ? { tagged_users: updatedItem.tagged_users } : {}),
  };
  return isEqual(currentItem, mergedItem) ? currentItem : mergedItem;
};

const aggregatedCommentsReducer = (state: State = initialState, action: Action): State => {
  if (action.type === 'RESOURCE_FETCH_COMPLETE' || action.type === 'RESOURCE_FETCH_MORE_COMPLETE') {
    const {
      payload: { data, name },
    } = action;
    if (
      name === 'AggregatedCommentFeedResource' ||
      name === 'AggregatedCommentReplyFeedResource' ||
      name === 'DidItCommentsResource'
    ) {
      const items = data || [];
      const newItems = items.reduce((map, item) => {
        map[item.id] = mergeAggregatedComment(state[item.id], item);
        return map;
      }, {});
      return {
        ...state,
        ...newItems,
      };
    } else if (name === 'UnifiedCommentsResource' && data && data.length > 0) {
      const items = data.filter((item) => item.type === 'aggregatedcomment');
      const newItems = items.reduce((map, item) => {
        map[item.id] = mergeAggregatedComment(state[item.id], item);
        return map;
      }, {});

      return { ...state, ...newItems };
    } else if (name === 'UnifiedCommentsPreviewResource' && data && data.length > 0) {
      // Only one comment is returned from resource
      const item = data[0].aggregated_comment;
      const newItem = item ? { [item.id]: mergeAggregatedComment(state[item.id], item) } : {};

      // Some comments will also have a creator reply
      const creatorReplyItem = data[0].creator_reply;
      const newCreatorReplyItem = creatorReplyItem
        ? {
            [creatorReplyItem.id]: mergeAggregatedComment(
              state[creatorReplyItem.id],
              creatorReplyItem,
            ),
          }
        : {};
      return { ...state, ...newItem, ...newCreatorReplyItem };
    }
  } else if (action.type === 'AGGREGATED_COMMENT_CREATED') {
    const {
      payload: { aggregatedComment },
    } = action;
    return {
      ...state,
      [aggregatedComment.id]: aggregatedComment,
    };
  } else if (
    action.type === 'AGGREGATED_COMMENT_DELETED' ||
    action.type === 'AGGREGATED_COMMENT_HIDDEN'
  ) {
    const {
      payload: { aggregatedCommentId },
    } = action;
    const nextState = { ...state };
    delete nextState[aggregatedCommentId];
    return nextState;
  } else if (action.type === 'AGGREGATED_COMMENT_UPDATED') {
    const {
      payload: { data },
    } = action;

    return {
      ...state,
      [data.id]: mergeAggregatedComment(state[data.id], data),
    };
  } else if (action.type === 'AGGREGATED_COMMENT_LIKE_TOGGLED') {
    const {
      payload: { data },
    } = action;
    const currentItem = state[data.id];
    const likeCount = currentItem.reaction_counts[PCONST.thrift.ReactionType.LIKE] || 0;
    const updatedReactionsCounts = {
      [PCONST.thrift.ReactionType.LIKE]: !!data.reaction_by_me ? likeCount + 1 : likeCount - 1,
    };
    const updatedItem = {
      ...currentItem,
      reaction_by_me: data.reaction_by_me,
      reaction_counts: updatedReactionsCounts,
    };
    return {
      ...state,
      [data.id]: updatedItem,
    };
  } else if (action.type === 'UPDATE_AGGREGATED_COMMENT_COMMENT_COUNT') {
    const {
      payload: { increment, id },
    } = action;
    const comment = state[id];

    const updatedItem = {
      ...comment,
      comment_count: increment ? comment.comment_count + 1 : comment.comment_count - 1,
    };
    return {
      ...state,
      [id]: updatedItem,
    };
  }
  return state;
};

export default aggregatedCommentsReducer;
